/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

class SCVoicePlayer extends HTMLElement {
  constructor() {
    super();
    this.audio = null;
    this.playButton = null;
    this.playIcon = null;
    this.pauseIcon = null;
    this.timeDisplay = null;
    this.waveBars = [];
    this._onTimeUpdate = this._onTimeUpdate.bind(this);
    this._onPlay = this._onPlay.bind(this);
    this._onPause = this._onPause.bind(this);
    this._onEnded = this._onEnded.bind(this);
  }

  connectedCallback() {
    // read attributes
    const audioSrc = this.getAttribute("audio") || "";
    const avatarSrc = this.getAttribute("avatar") || "";
    const bgSrc = this.getAttribute("bg") || "";

    // main container
    const container = document.createElement("div");
    container.classList.add("sc-voice-container");

    // voice wrapper
    const voiceWrapper = document.createElement("div");
    voiceWrapper.classList.add("sc-voice-wrapper");

    // avatar image
    if (avatarSrc) {
      const avatarImg = document.createElement("img");
      avatarImg.classList.add("sc-voice-avatar");
      avatarImg.src = avatarSrc;
      voiceWrapper.appendChild(avatarImg);
    }

    if (bgSrc) {
      container.style.backgroundImage = `url("${bgSrc}")`;
    }

    // waves container
    const wavesContainer = document.createElement("div");
    wavesContainer.classList.add("sc-voice-waves");

    // create 20 waves
    for (let i = 1; i <= 16; i++) {
      const bar = document.createElement("div");
      bar.classList.add(`sc-voice-wave-${i}`, "sc-voice-wave-bar");
      wavesContainer.appendChild(bar);
      this.waveBars.push(bar);
    }
    voiceWrapper.appendChild(wavesContainer);

    // control wrapper
    this.controlWrapper = document.createElement("div");
    this.controlWrapper.classList.add("sc-voice-control-wrapper");

    // time
    this.timeDisplay = document.createElement("p");
    this.timeDisplay.classList.add("sc-voice-timeDisplay");
    this.timeDisplay.textContent = "0:00 / 3:00";
    this.controlWrapper.appendChild(this.timeDisplay);

    // controls
    this.playButton = document.createElement("div");
    this.playButton.classList.add("sc-voice-player");
    this.playButton.addEventListener("click", () => this._togglePlay());

    // icons
    this.playIcon = document.createElement("div");
    this.playIcon.classList.add("sc-voice-player-play");
    this.playIcon.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 330 330" xml:space="preserve"><path fill="currentColor" d="M37.728 328.12a15 15 0 0 0 15.222-.4l240-149.999a15 15 0 0 0 0-25.44L52.95 2.28A15 15 0 0 0 30 15v300a15 15 0 0 0 7.728 13.12"/></svg>

      `;

    this.pauseIcon = document.createElement("div");
    this.pauseIcon.classList.add("sc-voice-player-pause");
    this.pauseIcon.style.display = "none";
    this.pauseIcon.innerHTML = `
        <svg height="14" width="14" viewBox="0 0 38 54" xmlns="http://www.w3.org/2000/svg">
          <rect fill="currentColor" x="0" y="0" width="14" height="54" rx="7"></rect>
          <rect fill="currentColor" x="24" y="0" width="14" height="54" rx="7"></rect>
        </svg>
      `;

    this.playButton.appendChild(this.playIcon);
    this.playButton.appendChild(this.pauseIcon);
    this.controlWrapper.appendChild(this.playButton);

    // hide native audio element
    this.audio = document.createElement("audio");
    this.audio.src = audioSrc;
    this.audio.addEventListener("timeupdate", this._onTimeUpdate);
    this.audio.addEventListener("play", this._onPlay);
    this.audio.addEventListener("pause", this._onPause);
    this.audio.addEventListener("ended", this._onEnded);

    // everything in one container
    container.appendChild(voiceWrapper);
    container.appendChild(this.controlWrapper);
    this.appendChild(container);
    this.appendChild(this.audio);
  }

  disconnectedCallback() {
    // clean up shit
    if (this.audio) {
      this.audio.removeEventListener("timeupdate", this._onTimeUpdate);
      this.audio.removeEventListener("play", this._onPlay);
      this.audio.removeEventListener("pause", this._onPause);
      this.audio.removeEventListener("ended", this._onEnded);
    }
  }

  _togglePlay() {
    //pause all other voice players
    const voicePlayers = document.querySelectorAll("sc-voice-player");
    voicePlayers.forEach((player) => {
      if (player !== this) {
        player.audio.pause();
      }
    });

    if (!this.audio) return;
    if (this.audio.paused) {
      this.audio.play();
    } else {
      this.audio.pause();
    }
  }

  _onPlay() {
    this.playIcon.style.display = "none";
    this.pauseIcon.style.display = "flex";
  }

  _onPause() {
    this.playIcon.style.display = "flex";
    this.pauseIcon.style.display = "none";
  }

  _onEnded() {
    this.playIcon.style.display = "flex";
    this.pauseIcon.style.display = "none";
  }

  _onTimeUpdate() {
    const current = this.audio.currentTime;
    const duration = this.audio.duration;
    this.timeDisplay.textContent = `${this._formatTime(
      current
    )} / ${this._formatTime(duration)}`;

    if (!isNaN(duration)) {
      const fraction = current / duration;
      const barsToHighlight = Math.floor(fraction * this.waveBars.length);
      this.waveBars.forEach((bar, index) => {
        if (index < barsToHighlight) {
          bar.classList.add("active");
        } else {
          bar.classList.remove("active");
        }
      });
    }
  }

  _formatTime(sec) {
    if (isNaN(sec)) return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  }
}
customElements.define("sc-voice-player", SCVoicePlayer);
